import React from "react";

import { motion, useReducedMotion } from "framer-motion";

import { Trans, useTranslation } from "react-i18next";

import LegalPageLayout from "./LegalPageLayout";

export default function PrivacyPolicyPage() {
  const { t } = useTranslation("home");

  const prefersReducedMotion = useReducedMotion();

  const contentVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 14,
    },

    visible: {
      opacity: 1,
      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.55,

        delay: prefersReducedMotion ? 0 : 0.2,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /* =========================================================
     STANDARD PARAGRAPH
     ========================================================= */

  const paragraph = (key, fallback) => <p>{t(key, fallback)}</p>;

  /* =========================================================
     PARAGRAPH WITH <strong>
     ========================================================= */

  const strongParagraph = (key, fallback) => (
    <p>
      <Trans
        ns="home"
        i18nKey={key}
        defaults={fallback}
        components={{
          strong: <strong />,
        }}
      />
    </p>
  );

  return (
    <LegalPageLayout
      pageClassName="privacy-policy-page"
      kicker={t("legalPages.privacy.kicker", "LEGAL & PRIVACY")}
      title={t("legalPages.privacy.title", "Privacy Policy")}
      description={t(
        "legalPages.privacy.description",
        "Information about how personal data is collected, used and protected within the LEG-UP platform.",
      )}
    >
      <motion.div
        className="legal-page__document"
        initial="hidden"
        animate="visible"
        variants={contentVariants}
      >
        <div className="legal-page__updated">
          {t("legalPages.privacy.lastUpdated", "Last updated: 20 October 2026")}
        </div>

        {/* =====================================================
            1. INTRODUCTION
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.introduction.title",
              "1. Introduction",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.introduction.p1",
            "The LEG-UP platform (the “Platform”) is a digital environment developed within the LEG-UP project to support residential renovation and financing processes involving Homebuyers, Lenders, One-Stop Shops (OSS) and other authorised participants.",
          )}

          {paragraph(
            "legalPages.privacy.sections.introduction.p2",
            "This Privacy Policy explains how personal data may be collected, used, stored, shared and otherwise processed when you register for or use the Platform.",
          )}

          {strongParagraph(
            "legalPages.privacy.sections.introduction.p3",
            "Personal data processed through the Platform is handled in accordance with applicable data protection legislation, including the <strong>General Data Protection Regulation (EU) 2016/679 (GDPR)</strong>.",
          )}

          {paragraph(
            "legalPages.privacy.sections.introduction.p4",
            "The Platform is intended for users who are at least 18 years old.",
          )}
        </section>

        {/* =====================================================
            2. DATA CONTROLLER
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.controller.title",
              "2. Data Controller and Participating Organisations",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.controller.p1",
            "The organisation responsible for determining the purposes and means of processing personal data through the core LEG-UP Platform acts as the data controller for that processing. The applicable controller and privacy contact information are provided through the official LEG-UP project and Platform information.",
          )}

          {strongParagraph(
            "legalPages.privacy.sections.controller.p2",
            "Participating Lenders, One-Stop Shops and other organisations may process personal data for their own professional activities and, depending on the circumstances, <strong>may act as separate data controllers for that processing.</strong>",
          )}

          {paragraph(
            "legalPages.privacy.sections.controller.p3",
            "Where this is the case, the relevant organisation is responsible for providing any additional privacy information required in relation to its own processing activities.",
          )}
        </section>

        {/* =====================================================
            3. DATA COLLECTED
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.collection.title",
              "3. Personal Data We Collect",
            )}
          </h2>

          <h3>
            {t(
              "legalPages.privacy.sections.collection.accountTitle",
              "3.1 Account and contact information",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.collection.accountText",
            "Depending on your role, we may process information such as your name, email address, telephone number, country, role within an organisation, organisation details, authentication identifiers, account status and email verification information.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.collection.applicationTitle",
              "3.2 Application and financial information",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.collection.applicationText",
            "For Homebuyers and applicants, the Platform may process information required for a financing or renovation workflow, including household and applicant information, employment and income information, financial commitments, requested financing amounts, co-applicant information and other information included in a dossier or loan case.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.collection.propertyTitle",
              "3.3 Property and renovation information",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.collection.propertyText",
            "The Platform may process property information such as addresses, property characteristics, energy-performance information, renovation requirements, renovation plans, estimated costs, funding sources, subsidies, invoices and information relating to the progress of a renovation project.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.collection.documentsTitle",
              "3.4 Documents and extracted information",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.collection.documentsText",
            "Users may upload documents required for the relevant LEG-UP workflow. Depending on the document and the functionality enabled, the Platform may also process document metadata, versions, comments and structured information extracted from uploaded documents.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.collection.communicationTitle",
              "3.5 Communications and activity information",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.collection.communicationText",
            "Where you communicate through the Platform, we may process messages, notifications and information relating to the relevant workflow. We may also process technical and security information such as timestamps, authentication events, system activity and logs necessary for the secure operation of the Platform.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.collection.thirdPartyTitle",
              "3.6 Information about other persons",
            )}
          </h3>

          {strongParagraph(
            "legalPages.privacy.sections.collection.thirdPartyText",
            "If you provide personal data relating to another person, such as a co-applicant, <strong>you are responsible for ensuring that you are authorised to provide that information</strong> and that the relevant person has been appropriately informed where required.",
          )}
        </section>

        {/* =====================================================
            4. PURPOSES
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.purposes.title",
              "4. Why We Process Personal Data",
            )}
          </h2>

          <p>
            {t(
              "legalPages.privacy.sections.purposes.intro",
              "Personal data may be processed for the following purposes:",
            )}
          </p>

          <ul>
            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.1",
                "to create, authenticate and manage user accounts;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.2",
                "to provide the Platform features associated with the user's role;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.3",
                "to create and manage dossiers, loan cases, property information and renovation projects;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.4",
                "to enable authorised information and document exchange between Homebuyers, Lenders and One-Stop Shops;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.5",
                "to support communication, notifications and workflow coordination;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.6",
                "to maintain Platform security, detect misuse and investigate technical or security incidents;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.7",
                "to provide technical assistance and respond to support requests;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.8",
                "to maintain records required for administration, auditing, accountability and compliance;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.purposes.items.9",
                "to evaluate and improve the operation, reliability and usability of the Platform using appropriate statistical or aggregated information where possible.",
              )}
            </li>
          </ul>
        </section>

        {/* =====================================================
            5. LEGAL BASIS
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.legalBasis.title",
              "5. Legal Basis for Processing",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.legalBasis.p1",
            "The legal basis used for processing depends on the type of information, the purpose of the processing and the relationship between the user, LEG-UP and the relevant participating organisation.",
          )}

          <p>
            {t(
              "legalPages.privacy.sections.legalBasis.intro",
              "Depending on the circumstances, processing may be based on:",
            )}
          </p>

          <ul>
            <li>
              {t(
                "legalPages.privacy.sections.legalBasis.items.1",
                "the performance of a contract or the taking of steps requested by you before entering into a contract;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.legalBasis.items.2",
                "compliance with legal obligations applicable to the relevant controller;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.legalBasis.items.3",
                "legitimate interests relating to the secure, effective and appropriate operation of the Platform, where those interests are not overridden by your rights and interests;",
              )}
            </li>

            <li>
              {t(
                "legalPages.privacy.sections.legalBasis.items.4",
                "your consent where a particular processing activity specifically requires consent.",
              )}
            </li>
          </ul>

          {strongParagraph(
            "legalPages.privacy.sections.legalBasis.p2",
            "<strong>Acceptance of the Terms & Conditions does not automatically constitute consent for every processing activity.</strong> Where GDPR consent is required, it will be requested separately and must be freely given, specific, informed and unambiguous.",
          )}
        </section>

        {/* =====================================================
            6. SHARING
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.sharing.title",
              "6. Who Can Access Personal Data",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.sharing.p1",
            "Information within LEG-UP is made available according to the user's role, the relevant workflow and the relationships established between participating users and organisations.",
          )}

          {paragraph(
            "legalPages.privacy.sections.sharing.p2",
            "For example, information relating to a financing application may be made available to the participating Lender involved in that application, while renovation project information may be shared with an authorised One-Stop Shop involved in the relevant project.",
          )}

          {strongParagraph(
            "legalPages.privacy.sections.sharing.p3",
            "<strong>Personal data is not made generally public through the Platform and is not sold to advertisers or other third parties.</strong>",
          )}

          {paragraph(
            "legalPages.privacy.sections.sharing.p4",
            "Personal data may also be accessible to authorised technical personnel, service providers or project organisations where such access is necessary to operate, maintain, secure or support the Platform.",
          )}

          {paragraph(
            "legalPages.privacy.sections.sharing.p5",
            "Personal data may also be disclosed where required by applicable law, a competent authority or another binding legal obligation.",
          )}
        </section>

        {/* =====================================================
            7. AI / AUTOMATED PROCESSING
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.automation.title",
              "7. Automated and AI-Assisted Processing",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.automation.p1",
            "Where enabled, LEG-UP may use automated or AI-assisted tools to support specific Platform functions, such as document preparation, optical character recognition, document classification, information extraction or the generation of proposed structured values.",
          )}

          {strongParagraph(
            "legalPages.privacy.sections.automation.p2",
            "Such functionality is intended to assist users and authorised professionals. <strong>LEG-UP does not use these tools to automatically make a final lending decision on behalf of a participating Lender.</strong>",
          )}

          {paragraph(
            "legalPages.privacy.sections.automation.p3",
            "Where extracted or automatically generated information is presented to a user, it may require review, correction or confirmation before it is relied upon within the relevant workflow.",
          )}
        </section>

        {/* =====================================================
            8. RETENTION
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.retention.title",
              "8. How Long We Keep Personal Data",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.retention.p1",
            "Personal data is retained only for as long as reasonably necessary for the purposes for which it was collected, including the operation of the relevant LEG-UP workflow, security, administration and compliance with applicable legal obligations.",
          )}

          {paragraph(
            "legalPages.privacy.sections.retention.p2",
            "Different categories of information may be subject to different retention periods depending on their purpose, the relevant user's relationship with participating organisations and applicable legal or contractual requirements.",
          )}

          {paragraph(
            "legalPages.privacy.sections.retention.p3",
            "Where a user requests account closure or deletion of personal data, the request will be assessed in accordance with applicable data protection law. Certain information may need to be retained where this is necessary to comply with legal obligations, establish or defend legal claims, maintain audit records or protect the security and integrity of the Platform.",
          )}
        </section>

        {/* =====================================================
            9. SECURITY
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.security.title",
              "9. Confidentiality, Security and Data Transfers",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.security.p1",
            "Appropriate technical and organisational measures are used to protect personal data against accidental or unlawful destruction, loss, alteration, unauthorised disclosure or unauthorised access.",
          )}

          {strongParagraph(
            "legalPages.privacy.sections.security.p2",
            "Access to Platform information is restricted according to authorised roles and permissions. However, <strong>no information system or Internet-based service can guarantee absolute security.</strong>",
          )}

          {paragraph(
            "legalPages.privacy.sections.security.p3",
            "Persons authorised to process personal data are expected to maintain appropriate confidentiality and to use the information only for authorised purposes.",
          )}

          {paragraph(
            "legalPages.privacy.sections.security.p4",
            "Where an external service provider processes personal data on behalf of the relevant controller, appropriate contractual and organisational safeguards are required.",
          )}

          {paragraph(
            "legalPages.privacy.sections.security.p5",
            "Where personal data is transferred outside the European Economic Area, such transfers will take place only where permitted under applicable data protection law and, where required, subject to appropriate safeguards.",
          )}
        </section>

        {/* =====================================================
            10. ACCURACY
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.accuracy.title",
              "10. Accuracy of Information",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.accuracy.p1",
            "Users are responsible for providing information that is, to the best of their knowledge, accurate, current and complete.",
          )}

          {paragraph(
            "legalPages.privacy.sections.accuracy.p2",
            "Where functionality allows, users should update information that becomes inaccurate or outdated. Information provided or validated by a participating organisation remains subject to that organisation's own professional responsibilities.",
          )}
        </section>

        {/* =====================================================
            11. GDPR RIGHTS
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.rights.title",
              "11. Your Data Protection Rights",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.rights.intro",
            "Subject to the conditions and limitations set out in the GDPR, you may have the following rights in relation to your personal data:",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.accessTitle",
              "11.1 Right of access",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.accessText",
            "You may request confirmation as to whether personal data concerning you is being processed and request access to that data and related information.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.rectificationTitle",
              "11.2 Right to rectification",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.rectificationText",
            "You may request correction of inaccurate personal data and completion of incomplete personal data.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.erasureTitle",
              "11.3 Right to erasure",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.erasureText",
            "You may request deletion of your personal data in circumstances provided by applicable data protection law. This right is not absolute and certain information may need to be retained where a lawful reason for retention applies.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.restrictionTitle",
              "11.4 Right to restriction of processing",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.restrictionText",
            "You may request that processing of your personal data be restricted in circumstances provided by the GDPR.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.portabilityTitle",
              "11.5 Right to data portability",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.portabilityText",
            "Where the applicable legal conditions are met, you may request personal data you have provided in a structured, commonly used and machine-readable format and may request that it be transmitted to another controller where technically feasible.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.objectTitle",
              "11.6 Right to object",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.objectText",
            "Where processing is based on legitimate interests or another legal basis for which the right to object applies, you may object to the processing on grounds relating to your particular situation.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.withdrawTitle",
              "11.7 Right to withdraw consent",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.withdrawText",
            "Where processing is based on your consent, you may withdraw that consent at any time. Withdrawal does not affect the lawfulness of processing carried out before consent was withdrawn.",
          )}

          <h3>
            {t(
              "legalPages.privacy.sections.rights.complaintTitle",
              "11.8 Right to lodge a complaint",
            )}
          </h3>

          {paragraph(
            "legalPages.privacy.sections.rights.complaintText",
            "You have the right to lodge a complaint with the competent data protection supervisory authority if you believe that your personal data has been processed in violation of applicable data protection law.",
          )}
        </section>

        {/* =====================================================
            12. COOKIES
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.cookies.title",
              "12. Cookies and Similar Technologies",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.cookies.p1",
            "The Platform may use cookies or similar technologies that are necessary for security, authentication, user preferences and the proper operation of the service.",
          )}

          {paragraph(
            "legalPages.privacy.sections.cookies.p2",
            "Where optional analytics or similar technologies are used, they will be managed in accordance with the applicable legal requirements and the choices made available to users.",
          )}

          {paragraph(
            "legalPages.privacy.sections.cookies.p3",
            "Further information about the use of cookies is provided through the LEG-UP cookie information and consent controls.",
          )}
        </section>

        {/* =====================================================
            13. CHANGES
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t(
              "legalPages.privacy.sections.changes.title",
              "13. Changes to this Privacy Policy",
            )}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.changes.p1",
            "This Privacy Policy may be updated to reflect changes to the Platform, processing activities, project arrangements or applicable legal requirements.",
          )}

          {paragraph(
            "legalPages.privacy.sections.changes.p2",
            "When this Privacy Policy is updated, the date shown at the top of this page will be revised. Where appropriate, users may also be informed of material changes through the Platform.",
          )}
        </section>

        {/* =====================================================
            14. CONTACT
            ===================================================== */}

        <section className="legal-page__section">
          <h2>
            {t("legalPages.privacy.sections.contact.title", "14. Contact")}
          </h2>

          {paragraph(
            "legalPages.privacy.sections.contact.p1",
            "If you have questions about this Privacy Policy, the processing of your personal data or the exercise of your data protection rights, please use the privacy contact information provided through the official LEG-UP website or Platform.",
          )}
        </section>
      </motion.div>
    </LegalPageLayout>
  );
}
